import React, {FC, useEffect, useRef, useState} from 'react'
import {useNavigate, useSearchParams} from 'react-router-dom'
import useAppToast from 'src/hooks/useAppToast'
import {fetchUserAccountDetailsAsync} from 'src/redux/actions/accountActions'
import { handleOauthForConnectPlatformAsync, loginByURL } from 'src/redux/actions/authActions'
import {fetchPlatformsAsync} from 'src/redux/actions/platformActions'
import { setIsLoggedIn } from 'src/redux/slices/authSlice'
import {useAppDispatch} from 'src/redux/storeHooks'
import Loader from 'src/utils/Loader'
import {
  accessSessionTokenKeyName,
  refreshSessionTokenKeyName,
  walmartInstallationOpenGuardKey,
  walmartInstallationResponseKey,
} from 'src/utils/constants';
import {
  captureWalmartMarketplaceHandoffFromSearch,
  getUserIdFromAuthPayload,
  getWalmartPlatformIdFromPayload,
  redirectToWalmartMarketplaceAuthIfNeeded,
} from 'src/utils/walmartMarketplaceHandoff';

const LoginByURL: FC = () => {
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { errorToast, warningToast } = useAppToast()
  const [loader, setLoader] = useState<boolean>(false);
  const hasHandledRef = useRef(false)
  const token = searchParams.get('token') 
  const walmartCallbackUri = searchParams.get('walmartCallbackUri')
  const clientType = searchParams.get('clientType')

  const recordWalmartResponse = async (callbackUrl: string, currentClientType: string | null) => {
    const startedAt = new Date().toISOString()

    try {
      const response = await fetch(callbackUrl, {
        method: 'GET',
        credentials: 'include',
      })
      const contentType = response.headers.get('content-type') || ''
      let responseBody: unknown = null

      if (contentType.includes('application/json')) {
        responseBody = await response.json()
      } else {
        responseBody = await response.text()
      }

      const record = {
        startedAt,
        finishedAt: new Date().toISOString(),
        clientType: currentClientType,
        callbackUrl,
        ok: response.ok,
        redirected: response.redirected,
        status: response.status,
        statusText: response.statusText,
        type: response.type,
        headers: {
          contentType,
        },
        body: responseBody,
      }

      localStorage.setItem(walmartInstallationResponseKey, JSON.stringify(record))
      console.log('Walmart installation callback response', record)

      if (!response.ok) {
        warningToast(`Walmart callback returned ${response.status}`)
      }
    } catch (error: any) {
      const record = {
        startedAt,
        finishedAt: new Date().toISOString(),
        clientType: currentClientType,
        callbackUrl,
        ok: false,
        errorMessage: error?.message || 'Unable to call Walmart callback URL',
        errorName: error?.name || 'Error',
      }

      localStorage.setItem(walmartInstallationResponseKey, JSON.stringify(record))
      console.error('Walmart installation callback failed', record)
      errorToast(record.errorMessage)
    }
  }
  
  useEffect(() => {
    if (hasHandledRef.current) {
      return
    }

    if (token) {
      hasHandledRef.current = true
      setLoader(true);
      dispatch(loginByURL({ token }))
          .then((response) => {
          if (response?.payload?.status_code === 200) {
              const data = response.payload.data;
              if (data?.access) {
              localStorage.setItem(accessSessionTokenKeyName, data.access);
              localStorage.setItem(refreshSessionTokenKeyName, data.refresh);
              dispatch(setIsLoggedIn({isLoggedIn: true}));
              const nextUrl = data.is_onboarding_completed === false 
                  ? `/pricing/${data.platform_id}` 
                  : `/dashboard`;
              navigate(nextUrl, { replace: true });
              
              } else {
              warningToast(data?.massage || data?.message);
              navigate('*');
              }
          } else {
              errorToast(response?.payload?.data?.message);
              navigate('*');
          }
          })
          .finally(() =>setLoader(false));
      return;
    }

    if (walmartCallbackUri) {
      hasHandledRef.current = true

      ;(async () => {
        let callbackSearchParams = ''
        try {
          callbackSearchParams = new URL(walmartCallbackUri).searchParams.toString()
        } catch (error) {
          errorToast('Walmart authorization URL is missing or invalid.')
          navigate('/auth/login?source=WALMART', {replace: true})
          return
        }

        const handoffStatus = captureWalmartMarketplaceHandoffFromSearch(
          `source=WALMART&${callbackSearchParams}&walmartCallbackUri=${encodeURIComponent(walmartCallbackUri)}${
            clientType ? `&clientType=${encodeURIComponent(clientType)}` : ''
          }`
        )

        if (handoffStatus === 'invalid') {
          errorToast('Walmart authorization URL is missing or invalid.')
          navigate('/auth/login?source=WALMART', {replace: true})
          return
        }

        if (!localStorage.getItem(accessSessionTokenKeyName)) {
          const loginParams = new URLSearchParams()
          loginParams.append('source', 'WALMART')
          loginParams.append('walmartCallbackUri', walmartCallbackUri)
          if (clientType) {
            loginParams.append('clientType', clientType)
          }
          navigate(`/auth/login?${loginParams.toString()}`, {replace: true})
          return
        }

        const userResponse = await dispatch(fetchUserAccountDetailsAsync())
        const userId = getUserIdFromAuthPayload(userResponse?.payload)
        const walmartPlatformResponse = await dispatch(fetchPlatformsAsync({search: 'WALMART'}))
        const walmartPlatformId = getWalmartPlatformIdFromPayload(walmartPlatformResponse?.payload)
        if (await redirectToWalmartMarketplaceAuthIfNeeded(
          (params) => dispatch(handleOauthForConnectPlatformAsync(params)),
          {user_id: userId, platform_id: walmartPlatformId}
        )) {
          return
        }

        setLoader(true)
        const guardValue = `${window.location.pathname}${window.location.search}`
        const lastOpenedValue = sessionStorage.getItem(walmartInstallationOpenGuardKey)

        if (lastOpenedValue !== guardValue) {
          sessionStorage.setItem(walmartInstallationOpenGuardKey, guardValue)
          window.open(walmartCallbackUri, '_blank', 'noopener,noreferrer')
        }

        recordWalmartResponse(walmartCallbackUri, clientType)
          .finally(() => setLoader(false))
      })()
      return;
    }

    if (!token) {
        hasHandledRef.current = true
        navigate('*');
        return;
    }
    }, [clientType, dispatch, errorToast, navigate, token, walmartCallbackUri, warningToast]);
  if (loader) return <Loader />
  return null
}
export default LoginByURL
